class ArtLaserMegaMenu {
  constructor(root) {
    this.root = root;
    this.items = [...root.querySelectorAll('[data-ag-mega]')];
    this.closeTimers = new WeakMap();
    this.animationTimers = new WeakMap();
    this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.duration = Number.parseFloat(getComputedStyle(root).getPropertyValue('--ag-mega-duration')) || 250;
    this.bind();
  }

  bind() {
    this.items.forEach((details) => {
      const trigger = details.querySelector(':scope > summary');
      if (!trigger) return;

      details.addEventListener('pointerenter', () => {
        if (this.hoverQuery.matches) this.open(details);
      });

      details.addEventListener('pointerleave', () => {
        if (this.hoverQuery.matches) this.scheduleClose(details);
      });

      details.addEventListener('focusin', () => this.open(details));
      details.addEventListener('focusout', (event) => {
        if (!details.contains(event.relatedTarget)) this.scheduleClose(details, 60);
      });

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.hoverQuery.matches) {
          this.open(details);
          return;
        }
        if (details.open && details.dataset.closing !== 'true') {
          this.close(details, false);
        } else {
          this.open(details);
        }
      });

      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close(details, true);
        }
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeAll(true);
    });

    document.addEventListener('pointerdown', (event) => {
      if (!this.root.contains(event.target)) this.closeAll(false);
    });
  }

  open(details) {
    this.cancelClose(details);
    this.cancelAnimation(details);
    details.removeAttribute('data-closing');
    this.items.forEach((item) => {
      if (item !== details) this.close(item, false, true);
    });
    details.open = true;
    details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('ag-mega-menu-open');
  }

  scheduleClose(details, delay = 140) {
    this.cancelClose(details);
    const timer = window.setTimeout(() => this.close(details, false), delay);
    this.closeTimers.set(details, timer);
  }

  cancelClose(details) {
    const timer = this.closeTimers.get(details);
    if (timer) window.clearTimeout(timer);
  }

  cancelAnimation(details) {
    const timer = this.animationTimers.get(details);
    if (timer) window.clearTimeout(timer);
  }

  close(details, restoreFocus, immediate = false) {
    this.cancelClose(details);
    if (!details.open) return;
    const trigger = details.querySelector(':scope > summary');
    trigger?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) trigger?.focus();

    const finish = () => {
      details.open = false;
      details.removeAttribute('data-closing');
      if (!this.items.some((item) => item.open)) document.body.classList.remove('ag-mega-menu-open');
    };

    if (immediate) {
      this.cancelAnimation(details);
      finish();
      return;
    }

    details.dataset.closing = 'true';
    this.cancelAnimation(details);
    const timer = window.setTimeout(finish, this.duration);
    this.animationTimers.set(details, timer);
  }

  closeAll(restoreFocus) {
    const openItem = this.items.find((item) => item.open);
    this.items.forEach((item) => this.close(item, false));
    if (restoreFocus) openItem?.querySelector(':scope > summary')?.focus();
  }
}

const initArtLaserMegaMenus = (scope = document) => {
  scope.querySelectorAll('[data-ag-mega-root]').forEach((root) => {
    if (root.dataset.agMegaReady === 'true') return;
    root.dataset.agMegaReady = 'true';
    new ArtLaserMegaMenu(root);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initArtLaserMegaMenus());
} else {
  initArtLaserMegaMenus();
}

document.addEventListener('shopify:section:load', (event) => initArtLaserMegaMenus(event.target));
